import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../lib/api';
import { useIdeas } from '../hooks/useIdeas';
import useVoiceInput from '../hooks/useVoiceInput';
import MicButton from '../components/voice/MicButton';
import VoiceStatus from '../components/voice/VoiceStatus';

export default function CapturePage() {
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showTextarea, setShowTextarea] = useState(false);
  const { createIdea } = useIdeas();
  const navigate = useNavigate();
  // Each recorded clip returns one clean chunk; just append it to the draft.
  const handleTranscript = useCallback((text) => {
    setInput((p) => (p.trim() ? `${p.trim()} ${text}` : text));
  }, []);
  const voice = useVoiceInput({ onTranscript: handleTranscript });

  const saveIdea = async () => {
    // A recognizer left open can append text after the POST fires.
    voice.stop();
    if (!input.trim()) return null;
    setSaving(true);
    try {
      const idea = await createIdea(input.trim());
      api.post(`/ideas/${idea.id}/auto-title/`).catch(() => {});
      return idea;
    } catch {
      toast.error('Failed to save idea');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Speaking an idea always drops you straight into developing it — there's no
  // separate "just save" step. Saving is implied by starting to develop.
  const handleDevelop = async (e) => {
    e?.preventDefault();
    const idea = await saveIdea();
    if (idea) navigate(`/ideas/${idea.id}/chat`);
  };

  const handleAllIdeas = () => {
    if (input.trim() && !window.confirm('Discard this unsaved idea?')) return;
    voice.stop();
    navigate('/ideas');
  };

  const textareaVisible = showTextarea || !voice.supported || Boolean(input);
  const busy = saving || !input.trim();

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent mb-1">
        Develop an Idea
      </h1>
      <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
        Say it out loud &mdash; we&apos;ll start shaping it together.
      </p>

      <form onSubmit={handleDevelop} className="space-y-6">
        <div className="flex flex-col items-center gap-3 py-6">
          <MicButton
            size="hero"
            supported={voice.supported}
            listening={voice.listening}
            onStart={voice.start}
            onStop={voice.stop}
            disabled={saving || voice.transcribing}
          />
          <VoiceStatus
            supported={voice.supported}
            listening={voice.listening}
            transcribing={voice.transcribing}
            error={voice.error}
          />
        </div>

        {textareaVisible ? (
          <div className="space-y-1">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="What's on your mind?"
              rows={8}
              autoFocus={!voice.supported}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-transparent shadow-sm focus:shadow-md resize-none text-lg placeholder:italic dark:placeholder-gray-500 transition-shadow"
            />
          </div>
        ) : (
          <div className="text-center space-y-1">
            <button
              type="button"
              onClick={() => setShowTextarea(true)}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              or type instead
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 md:flex md:justify-end md:gap-3">
          <button
            type="submit"
            disabled={busy}
            className="px-3 md:px-4 py-3 md:py-2 text-sm md:text-base leading-tight bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg font-medium shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Develop'}
          </button>
          <button
            type="button"
            onClick={handleAllIdeas}
            className="px-3 md:px-4 py-3 md:py-2 text-sm md:text-base leading-tight text-gray-600 dark:text-gray-400 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
          >
            All Ideas
          </button>
        </div>
      </form>
    </div>
  );
}
